import { useCallback, useState } from 'react'
import { Typography } from '~/components/common/Typography'
import { ManageScopeModal } from '~/components/monitoring/ManageScopeModal'

interface Integration {
  conn_type: string
  connected: boolean
  metadata: { allowed_indices?: string[] } | null
}

function GearIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox='0 0 16 16' fill='none' className={className}>
      <path
        d='M8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M13.05 10.13a1.1 1.1 0 0 0 .22 1.213l.04.04a1.334 1.334 0 1 1-1.887 1.887l-.04-.04a1.1 1.1 0 0 0-1.213-.22 1.1 1.1 0 0 0-.667 1.007v.113a1.333 1.333 0 1 1-2.666 0v-.06A1.1 1.1 0 0 0 6.12 13.05a1.1 1.1 0 0 0-1.213.22l-.04.04a1.334 1.334 0 1 1-1.887-1.887l.04-.04a1.1 1.1 0 0 0 .22-1.213 1.1 1.1 0 0 0-1.007-.667H2.12a1.333 1.333 0 1 1 0-2.666h.06A1.1 1.1 0 0 0 3.187 6.12a1.1 1.1 0 0 0-.22-1.213l-.04-.04A1.334 1.334 0 1 1 4.814 2.98l.04.04a1.1 1.1 0 0 0 1.213.22h.053a1.1 1.1 0 0 0 .667-1.007V2.12a1.333 1.333 0 1 1 2.666 0v.06a1.1 1.1 0 0 0 .667 1.007 1.1 1.1 0 0 0 1.213-.22l.04-.04a1.334 1.334 0 1 1 1.887 1.887l-.04.04a1.1 1.1 0 0 0-.22 1.213v.053a1.1 1.1 0 0 0 1.007.667h.113a1.333 1.333 0 1 1 0 2.666h-.06a1.1 1.1 0 0 0-1.007.667Z'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function ChevronDownIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox='0 0 20 20' fill='none' className={className}>
      <path
        d='M5 7.5L10 12.5L15 7.5'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

interface MonitoringMonitoredScopeContentProps {
  workspaceUuid: string
  integrations: Integration[]
  refreshIntegrations: () => void
  isCompact?: boolean
  autoOpenManageScope?: boolean
}

export function MonitoringMonitoredScopeContent({
  workspaceUuid,
  integrations,
  refreshIntegrations,
  isCompact = false,
  autoOpenManageScope = false
}: MonitoringMonitoredScopeContentProps) {
  const [selectedPlatform, setSelectedPlatform] = useState('splunk_credentials')
  const [isPlatformDropdownOpen, setIsPlatformDropdownOpen] = useState(false)
  const [manageScopeOpen, setManageScopeOpen] = useState(autoOpenManageScope)

  const connectedProviders = integrations.filter(i => {
    if (i.conn_type === 'splunk_credentials') return i.connected
    return false
  })

  const platformLabelMap: Record<string, string> = {
    splunk_credentials: 'Splunk'
  }

  const selectedIntegration = integrations.find(i => i.conn_type === selectedPlatform)
  const allowedIndices = Array.isArray(selectedIntegration?.metadata?.allowed_indices)
    ? [...new Set(selectedIntegration.metadata!.allowed_indices)].sort((a, b) => a.localeCompare(b))
    : []

  const handleManageScopeSaved = useCallback(() => {
    refreshIntegrations()
  }, [refreshIntegrations])

  return (
    <>
      <div
        className={
          isCompact ? 'h-full overflow-y-auto py-4 px-3' : 'h-full overflow-y-auto py-6 px-[48px]'
        }
      >
        <div className={`flex flex-col ${isCompact ? 'gap-3' : 'gap-8'}`}>
          {!isCompact && (
            <Typography
              fontFamily='geist'
              fontSize='titleLg'
              fontWeight='bold'
              className='shrink-0 text-[#1e1e1e]'
            >
              Monitored Scope
            </Typography>
          )}

          {connectedProviders.length === 0 ? (
            <Typography fontFamily='geist' fontSize='bodyMd' className='text-[rgba(0,0,0,0.65)]'>
              No monitoring connections configured. Set up a connection in the Connections tab
              first.
            </Typography>
          ) : (
            <div
              className={[
                'flex flex-col rounded-2xl border border-white bg-[rgba(255,255,255,0.5)]',
                isCompact ? 'gap-2 p-3' : 'gap-6 py-6 pl-6 pr-8'
              ].join(' ')}
            >
              <div className='flex flex-col gap-0'>
                <div className='flex items-center justify-between'>
                  <div className='relative'>
                    <button
                      type='button'
                      onClick={() => setIsPlatformDropdownOpen(prev => !prev)}
                      className={`flex items-center justify-between rounded-xl border border-[#d6d6d6] ${isCompact ? 'h-8 w-[160px] px-2.5' : 'h-12 w-[232px] px-4 py-3'}`}
                    >
                      {isCompact ? (
                        <Typography fontFamily='geist' fontSize='bodySm' fontWeight='medium'>
                          {platformLabelMap[selectedPlatform] ?? selectedPlatform}
                        </Typography>
                      ) : (
                        <div className='flex flex-col items-start gap-[4px]'>
                          <span className='font-[Geist_Mono] text-[11px] leading-normal tracking-[-0.33px] text-[rgba(0,0,0,0.45)]'>
                            Platform
                          </span>
                          <Typography
                            fontFamily='geist'
                            fontSize='bodyMdMedium'
                            fontWeight='medium'
                          >
                            {platformLabelMap[selectedPlatform] ?? selectedPlatform}
                          </Typography>
                        </div>
                      )}
                      <ChevronDownIcon
                        className={
                          isCompact
                            ? 'size-[16px] text-[rgba(0,0,0,0.65)]'
                            : 'size-[20px] text-[rgba(0,0,0,0.65)]'
                        }
                      />
                    </button>
                    {isPlatformDropdownOpen ? (
                      <div
                        className={`absolute left-0 z-10 rounded-xl border border-[#eaeaea] bg-white shadow-md ${isCompact ? 'top-9 w-[160px]' : 'top-[52px] w-[232px]'}`}
                      >
                        {connectedProviders.map(p => (
                          <button
                            key={p.conn_type}
                            type='button'
                            className='w-full px-[16px] py-[10px] text-left hover:bg-[rgba(0,0,0,0.04)]'
                            onClick={() => {
                              setSelectedPlatform(p.conn_type)
                              setIsPlatformDropdownOpen(false)
                            }}
                          >
                            <Typography fontFamily='geist' fontSize='bodySm' fontWeight='medium'>
                              {platformLabelMap[p.conn_type] ?? p.conn_type}
                            </Typography>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <button
                    type='button'
                    className='flex items-center gap-[8px]'
                    onClick={() => setManageScopeOpen(true)}
                  >
                    <GearIcon
                      className={
                        isCompact ? 'size-[16px] text-[#5b5bd5]' : 'size-[18px] text-[#5b5bd5]'
                      }
                    />
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      fontWeight='medium'
                      className='text-[#5b5bd5]'
                    >
                      Manage Scope
                    </Typography>
                  </button>
                </div>

                <div className={isCompact ? 'py-1' : 'px-2 py-4'}>
                  <div className='h-px w-full bg-[#eaeaea]' />
                </div>
              </div>

              {allowedIndices.length > 0 ? (
                <div className={`flex flex-col ${isCompact ? 'gap-1' : 'gap-3'}`}>
                  {allowedIndices.map(indexName => (
                    <div
                      key={indexName}
                      className={`flex items-center rounded-xl bg-[rgba(0,0,0,0.02)] ${isCompact ? 'h-8 pl-2.5' : 'h-10 pl-3'}`}
                    >
                      <Typography
                        fontFamily='geist'
                        fontSize={isCompact ? 'bodySm' : 'bodyMdMedium'}
                        fontWeight='medium'
                        className='text-[rgba(0,0,0,0.88)]'
                      >
                        {indexName}
                      </Typography>
                    </div>
                  ))}
                </div>
              ) : (
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-[rgba(0,0,0,0.45)]'
                >
                  No indices selected. Click "Manage Scope" to configure monitored indices.
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>

      <ManageScopeModal
        open={manageScopeOpen}
        onClose={() => setManageScopeOpen(false)}
        workspaceUuid={workspaceUuid}
        currentAllowedIndices={allowedIndices}
        onSaved={handleManageScopeSaved}
      />
    </>
  )
}
